<template>
  <div class="compair-layout--box">
    <div class="compair-layout--box-header">
      <h3>Filter</h3>
      <div class="vx-row">
        <div class="vx-col w-full">
          <DataInput
            v-model="filter.campaignID"
            type="model"
            label="Anzeigegruppen"
            name="google"
            :reduce="el => (el ? el.adGroupID : null)"
            primary-key="adGroupID"
            primary-key-type="Int64"
            gql-query="googleAdOverview"
            gql-query-attributes="page: 1, filter: { search: $search }, adGroup: true"
            gql-filter-type="objectFilter"
            model-label="adGroupName"
            :select-label="el => el.adGroupName"
            class="mt-4"
          />
        </div>
        <div class="vx-col w-full lg:w-1/2 mt-2">
          <DataInput
            v-model="filter.startDate"
            type="date"
            label="Von"
            name="from"
            :config="{
              altFormat: 'd.m.Y H:i',
              dateFormat: 'Y-m-d H:i'
            }"
          />
        </div>
        <div class="vx-col w-full lg:w-1/2 mt-2">
          <DataInput
            v-model="filter.endDate"
            type="date"
            label="Bis"
            name="to"
            :config="{
              altFormat: 'd.m.Y H:i',
              dateFormat: 'Y-m-d H:i'
            }"
          />
        </div>
        <div class="vx-col w-full lg:w-1/2 mt-2">
          <DataInput
            v-model="filter.metric"
            type="select"
            :options="[
              { code: 'CLICKS', label: 'Klicks' },
              { code: 'SPENT', label: 'Ausgegeben' },
              { code: 'CPC', label: 'CPC' },
              { code: 'IMPRESSIONS', label: 'Impressionen' },
              { code: 'CTR', label: 'CTR' }
            ]"
            placeholder="Wert"
            name="metric"
            label="Metrik"
            required
          />
        </div>
      </div>
    </div>
    <div class="compair-layout--box-body">
      <template v-if="filter.campaignID && datacollection">
        <GoogleStatChart
          ref="chart"
          :chart-data="datacollection"
          :height="250"
          :max="chartMaxY"
          :options="options"
        ></GoogleStatChart>
      </template>
      <template v-else> Bitte Google Kampagne auswählen. </template>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import moment from "moment";
import updateChartYLegendMixin from "@/mixins/updateChartYLegendMixin";
import DataInput from "@/components/ui/DataInput";
import GoogleStatChart from "./charts/GoogleStatChart";
import { fillMissingDates } from "../../helpers/fillMissingDates";

export default {
  components: {
    DataInput,
    GoogleStatChart
  },
  mixins: [updateChartYLegendMixin],
  props: {
    maxY: { type: Number, default: null }
  },
  data() {
    return {
      internalMaxY: 1,
      datacollection: null,
      filter: {
        campaignID: null,
        startDate: moment().subtract(7, "days"),
        endDate: null,
        metric: "CLICKS"
      }
    };
  },
  apollo: {
    googleAdOverview: {
      query: gql`
        query GoogleAd(
          $filter: GoogleAdFilter
          $datapointStart: Time!
          $datapointEnd: Time
        ) {
          googleAdOverview(filter: $filter, size: 200) {
            results {
              id
              adGroupName
              headlinePart1
              headlinePart2
              datapoints(from: $datapointStart, to: $datapointEnd) {
                date
                clicks
                costMicros
                avgCpc
                impressions
                ctr
              }
            }
          }
        }
      `,
      variables() {
        return {
          filter: {
            adGroupID: [+this.filter.campaignID]
          },
          datapointStart: this.filter.startDate,
          datapointEnd: this.filter.endDate
        };
      }
    }
  },
  computed: {
    chartMaxY() {
      return Math.max(this.internalMaxY, this.maxY);
    }
  },
  watch: {
    googleAdOverview: {
      handler(stats) {
        this.updateChart(stats);
      }
    },
    "filter.metric"() {
      if (this.datacollection && this.googleAdOverview) {
        this.updateChart(this.googleAdOverview);
      }
    }
  },
  methods: {
    updateChart(stats) {
      if (!stats.results || !this.filter.metric) {
        this.datacollection = null;
        return;
      }

      let labels = [];
      for (let dataset of stats.results) {
        dataset.datapoints.sort((a, b) =>
          a.date > b.date ? 1 : a.date < b.date ? -1 : 0
        );

        for (let data of dataset.datapoints) {
          let time = moment(data.date);
          if (labels.findIndex(val => val.diff(time) === 0) === -1) {
            labels.push(time);
          }
        }
      }
      labels.sort(function(a, b) {
        return a.isBefore(b) ? -1 : 1;
      });

      labels = fillMissingDates(labels);

      let metricField = {
        CLICKS: "clicks",
        SPENT: "costMicros",
        IMPRESSIONS: "impressions",
        CPC: "avgCpc",
        CTR: "ctr"
      }[this.filter.metric];

      let groupings = {};
      for (let i in stats.results) {
        let data = stats.results[i];
        let infoColContent = `${data.adGroupName} (${+i + 1})`;
        if (data.headlinePart1) {
          infoColContent = data.headlinePart1 + " - " + data.headlinePart2;
        }

        if (infoColContent && infoColContent.name) {
          infoColContent = infoColContent.name;
        }

        if (groupings[infoColContent || "n.A."] === undefined) {
          groupings[infoColContent || "n.A."] = [];
        }
      }

      let maxValue = 1;
      for (let label of labels) {
        for (let i in stats.results) {
          let dataset = stats.results[i];
          for (let data of dataset.datapoints) {
            let time = moment(data.date);
            if (time.diff(label) === 0) {
              let infoColContent = `${dataset.adGroupName} (${+i + 1})`;
              if (dataset.headlinePart1) {
                infoColContent =
                  dataset.headlinePart1 + " - " + dataset.headlinePart2;
              }

              if (
                this.filter.metric === "SPENT" ||
                this.filter.metric === "CPC"
              ) {
                groupings[infoColContent || "n.A."].push(
                  data[metricField] / 1000000
                );
              } else {
                groupings[infoColContent || "n.A."].push(data[metricField]);
              }
            }
          }
        }
      }

      let datasets = [];
      for (let grouping of Object.keys(groupings)) {
        let r = Math.floor(Math.random() * 255) + 1;
        let g = Math.floor(Math.random() * 255) + 1;
        let b = Math.floor(Math.random() * 255) + 1;
        datasets.push({
          label: grouping,
          borderColor: `rgb(${r}, ${g}, ${b})`,
          data: groupings[grouping],
          fill: true,
          backgroundColor: `rgba(${r}, ${g}, ${b}, 0.8)`,
          pointHoverBackgroundColor: "#000000",
          lineTension: 0.4
        });

        const currentMaxValue = Math.max.apply(Math, groupings[grouping]);
        if (currentMaxValue > maxValue) {
          maxValue = currentMaxValue;
        }
      }

      this.internalMaxY = maxValue;
      this.$emit("max-y-updated", maxValue);

      this.datacollection = {
        labels,
        datasets
      };

      this.$nextTick().then(() => {
        this.$refs.chart.updateChart();
      });
    }
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/components/bi/main";
</style>
